import { FC, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { ExportDetails } from "../../../../types/niffler";
import { resubmitExport } from "../../../../apis/niffler/export";
import { canDownloadExport, canResubmitExport } from "../utils/helpers";
import { useSnackbar } from "../../../../components/Snackbar";

// Helper function to format API error messages
const formatErrorMessage = (error: Error): string => {
  const { message } = error;

  // Handle RESUBMIT_TOO_SOON error with time extraction
  if (message.includes("RESUBMIT_TOO_SOON")) {
    const timeMatch = message.match(/(\d+)\s+(minute|minutes)/i);
    if (timeMatch) {
      const [, time] = timeMatch;
      const unit = parseInt(time) === 1 ? "minute" : "minutes";
      return `Export was recently resubmitted. Please wait ${time} ${unit} before trying again.`;
    }
    return "Export was recently resubmitted. Please wait a few minutes before trying again.";
  }

  // Return original message for other errors
  return message;
};

interface ExportActionsProps {
  exportDetails: ExportDetails;
  onViewDetails: (exportDetails: ExportDetails) => void;
  onActionComplete?: () => void;
}

export const ExportActions: FC<ExportActionsProps> = ({
  exportDetails,
  onViewDetails,
  onActionComplete,
}) => {
  const [resubmitting, setResubmitting] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  const handleDownload = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (exportDetails.s3url) {
      window.open(exportDetails.s3url, "_blank");
    }
  };

  const handleResubmit = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      setResubmitting(true);
      await resubmitExport(exportDetails.id);
      showSuccess("Export resubmitted successfully");
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? formatErrorMessage(error)
          : "Failed to resubmit export";
      showError(errorMessage);
    } finally {
      setResubmitting(false);
    }
  };

  const handleViewDetails = (event: React.MouseEvent) => {
    event.stopPropagation();
    onViewDetails(exportDetails);
  };

  const canDownload = canDownloadExport(exportDetails);
  const canResubmit = canResubmitExport(exportDetails);

  return (
    <Box display="flex" gap={0.5}>
      <Tooltip title="View details">
        <IconButton
          data-testid="export-view-details-btn"
          size="small"
          onClick={handleViewDetails}
          aria-label="View export details"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {canDownload && (
        <Tooltip title="Download export">
          <IconButton
            data-testid="settings-export-download-btn"
            size="small"
            onClick={handleDownload}
            aria-label="Download export"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {canResubmit && (
        <Tooltip title="Resubmit export">
          <IconButton
            data-testid="settings-export-resubmit-btn"
            size="small"
            onClick={handleResubmit}
            disabled={resubmitting}
            aria-label="Resubmit export"
          >
            {resubmitting ? (
              <CircularProgress size={16} />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
